import { useCallback, useState } from 'react'
import { ImportGroup } from '../api/bookmark'

export type ImportGroupKind = 'region' | 'list'

/** 서버가 묶음과 함께 주는 것 (resolve-shared-link 의 groupKind · ownerName) */
export interface ImportGroupMeta {
  groupKind?: ImportGroupKind
  ownerName?: string | null
}

/**
 * 고른 곳이 **한 리스트 안에만** 들어 있으면 그 리스트 이름.
 *
 * 공개 리스트에서 '성수' 하나만 골랐는데 플레이트리스트 이름이 다른 리스트
 * 이름으로 채워지면 이상하다. 같은 곳이 두 리스트에 다 있으면 어느 쪽인지
 * 모르므로 null (부르는 쪽이 원래 이름을 쓴다).
 */
export function singleListName(
  groups: ImportGroup[],
  selected: number[]
): string | null {
  if (selected.length === 0) return null
  const owners = groups.filter((g) => {
    const ids = new Set(g.place_ids)
    return selected.every((id) => ids.has(id))
  })
  return owners.length === 1 ? owners[0].name : null
}

/**
 * 묶음으로 나뉜 장소를 고르는 상태.
 *
 * 세 화면(온보딩·플레이트리스트 추가·시트)이 똑같은 일을 해서 한곳에 모았다.
 * 셋이 따로 들고 있을 때 한 군데만 고치고 나머지를 빠뜨리기 쉬웠다.
 *
 * **고르는 단위는 장소 하나**다. 묶음 체크는 그 안을 한꺼번에 켜고 끄는
 * 손잡이일 뿐이고, 담기는 것은 켜진 장소들이다. 그래서 '영통구에서 세 곳만'
 * 같은 고르기가 된다.
 */
export function useImportGroupSelection() {
  const [groups, setGroups] = useState<ImportGroup[] | null>(null)
  const [title, setTitle] = useState<string | null>(null)
  const [openedGroupId, setOpenedGroupId] = useState<string | null>(null)
  const [selected, setSelected] = useState<number[]>([])
  const [groupKind, setGroupKind] = useState<ImportGroupKind>('region')
  const [ownerName, setOwnerName] = useState<string | null>(null)

  const reset = useCallback(() => {
    setGroups(null)
    setTitle(null)
    setOpenedGroupId(null)
    setSelected([])
    setGroupKind('region')
    setOwnerName(null)
  }, [])

  /**
   * 묶음 화면을 켠다.
   *
   * picked 는 되살릴 때만 쓴다 — 기록하러 떠났다가 뒤로 돌아온 사람에게
   * 고른 것까지 돌려주려고. 새로 가져올 때는 비워 둔다.
   */
  const start = useCallback(
    (
      next: ImportGroup[],
      name: string | null,
      picked: number[] = [],
      meta: ImportGroupMeta = {}
    ) => {
      setGroups(next)
      setTitle(name)
      setOpenedGroupId(null)
      setSelected(picked)
      setGroupKind(meta.groupKind === 'list' ? 'list' : 'region')
      setOwnerName(meta.ownerName ?? null)
    },
    []
  )

  const togglePlace = useCallback((id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }, [])

  const toggleGroup = useCallback(
    (groupId: string) => {
      const group = groups?.find((g) => g.id === groupId)
      if (!group) return

      setSelected((prev) => {
        const on = new Set(prev)
        const allOn = group.place_ids.every((id) => on.has(id))
        group.place_ids.forEach((id) => (allOn ? on.delete(id) : on.add(id)))
        return Array.from(on)
      })
    },
    [groups]
  )

  /** groupId 를 주면 그 묶음만, 안 주면 전부 */
  const toggleAll = useCallback(
    (groupId?: string) => {
      if (!groups) return

      const target = groupId
        ? groups.filter((g) => g.id === groupId)
        : groups
      const ids = target.flatMap((g) => g.place_ids)

      setSelected((prev) => {
        const on = new Set(prev)
        const allOn = ids.every((id) => on.has(id))
        ids.forEach((id) => (allOn ? on.delete(id) : on.add(id)))
        return Array.from(on)
      })
    },
    [groups]
  )

  /**
   * 플레이트리스트 이름칸에 넣을 이름.
   * 공개 리스트에서 한 리스트 안의 곳만 골랐으면 그 리스트 이름, 아니면 title.
   */
  const folderName =
    groups && groupKind === 'list'
      ? singleListName(groups, selected) ?? title
      : title

  return {
    groups,
    title,
    groupKind,
    ownerName,
    folderName,
    openedGroupId,
    selected,
    setOpenedGroupId,
    start,
    reset,
    togglePlace,
    toggleGroup,
    toggleAll,
  }
}
